import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MessageCircle, Send, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setError("Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-24 sm:py-32 bg-[#0C1620] relative"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3A4F60]/50 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <p className="uppercase text-xs tracking-[0.25em] text-[#C5993A] mb-4">
            Kontakt
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-white">
            Jetzt Erstgespräch vereinbaren
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Column: Contact Form + Company Info */}
          <div className="space-y-8" data-testid="contact-form-container">
            {sent ? (
              <div data-testid="contact-success" className="bg-[#12202f] border border-[#C5993A]/30 rounded-lg p-10 text-center">
                <CheckCircle className="w-16 h-16 text-[#C5993A] mx-auto mb-6" />
                <h3 className="font-heading text-2xl text-white mb-3">Nachricht gesendet!</h3>
                <p className="text-[#8899AA]">Wir melden uns in Kürze bei Ihnen.</p>
                <Button
                  data-testid="send-another-button"
                  onClick={() => setSent(false)}
                  className="mt-6 bg-transparent border border-[#C5993A] text-[#C5993A] hover:bg-[#C5993A]/10"
                >
                  Weitere Nachricht senden
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <label className="block text-sm text-[#8899AA] mb-1.5">Name *</label>
                  <Input
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ihr vollständiger Name"
                    className="bg-[#12202f] border-[#3A4F60]/50 text-white placeholder:text-[#3A4F60] focus:border-[#C5993A] focus:ring-[#C5993A]"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-[#8899AA] mb-1.5">E-Mail *</label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="ihre@email.de"
                      className="bg-[#12202f] border-[#3A4F60]/50 text-white placeholder:text-[#3A4F60] focus:border-[#C5993A] focus:ring-[#C5993A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#8899AA] mb-1.5">Telefon</label>
                    <Input
                      data-testid="contact-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+49 ..."
                      className="bg-[#12202f] border-[#3A4F60]/50 text-white placeholder:text-[#3A4F60] focus:border-[#C5993A] focus:ring-[#C5993A]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#8899AA] mb-1.5">Nachricht *</label>
                  <Textarea
                    data-testid="contact-message-input"
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Wie können wir Ihnen helfen?"
                    rows={5}
                    className="bg-[#12202f] border-[#3A4F60]/50 text-white placeholder:text-[#3A4F60] focus:border-[#C5993A] focus:ring-[#C5993A] resize-none"
                  />
                </div>
                {error && <p data-testid="contact-error" className="text-red-400 text-sm">{error}</p>}
                <Button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="w-full bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white font-medium py-6 text-base rounded hover:from-[#8B6B20] hover:to-[#C5993A] transition-all border-0"
                >
                  {sending ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Wird gesendet...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Nachricht senden
                    </span>
                  )}
                </Button>
              </form>
            )}

            {/* Company Info - moved under form */}
            <div className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-8">
              <h4 className="font-heading text-lg text-white mb-3">VMBS UG</h4>
              <div className="text-[#8899AA] text-sm space-y-1 font-light">
                <p>Hr. Stefan Schäfer</p>
                <p>Ludwigstr. 9</p>
                <p>85298 Scheyern</p>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Info + QR */}
          <div className="space-y-8" data-testid="contact-info">
            <div className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-8">
              <h3 className="font-heading text-2xl text-white mb-6">Direkter Kontakt</h3>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#C5993A]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#C5993A]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8899AA] mb-1">Telefon</p>
                    <a
                      href="tel:+491729571525"
                      data-testid="phone-link"
                      className="text-white hover:text-[#C5993A] transition-colors text-lg"
                    >
                      +49 172 9571525
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#C5993A]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#C5993A]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8899AA] mb-1">E-Mail</p>
                    <a
                      href="mailto:info@vmbs.de"
                      data-testid="email-link"
                      className="text-white hover:text-[#C5993A] transition-colors text-lg"
                    >
                      info@vmbs.de
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#C5993A]/10 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-[#C5993A]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8899AA] mb-1">WhatsApp</p>
                    <a
                      href="https://wa.me/4915231495892"
                      data-testid="whatsapp-link"
                      className="text-white hover:text-[#C5993A] transition-colors text-lg"
                    >
                      +49 1523 1495892
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp QR Code */}
            <div className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-8 text-center">
              <h4 className="font-heading text-lg text-[#C5993A] mb-4">WhatsApp QR-Code scannen</h4>
              <div className="bg-white rounded-lg p-4 inline-block">
                <img
                  src="/images/whatsapp-qr.png"
                  alt="WhatsApp QR-Code für VMBS"
                  className="w-48 h-48 object-contain"
                  data-testid="whatsapp-qr-code"
                />
              </div>
              <p className="text-[#8899AA] text-sm mt-4">
                Scannen Sie den Code mit Ihrem Smartphone
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
